import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #bundlePriceObserver;
  #bundlePriceFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.#normalizeBundlePrices();
    this.#bundlePriceObserver = new MutationObserver(() => this.#normalizeBundlePrices());
    this.#bundlePriceObserver.observe(this, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.#bundlePriceObserver?.disconnect();
    if (this.#bundlePriceFrame) window.cancelAnimationFrame(this.#bundlePriceFrame);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.#findCartItemRow(line);

    const bundleUpdates = lineItemRow ? this.#buildBundleUpdates(lineItemRow, quantity) : null;

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      bundleUpdates,
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#findCartItemRow(line);
    const bundleUpdates = cartItemRowToRemove ? this.#buildBundleUpdates(cartItemRowToRemove, 0) : null;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      bundleUpdates,
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const hasBundleUpdates = Boolean(config.bundleUpdates && Object.keys(config.bundleUpdates).length > 0);
    const body = JSON.stringify(
      hasBundleUpdates
        ? {
            updates: config.bundleUpdates,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }
        : {
            line: line,
            quantity: quantity,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }
    );

    cartTotal?.shimmer();

    fetch(`${hasBundleUpdates ? Theme.routes.cart_update_url : Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        let parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const orphanUpdates = this.#buildOrphanWizardBundleUpdates(parsedResponseText);
        if (Object.keys(orphanUpdates).length > 0) {
          parsedResponseText = await this.#updateCartItems(orphanUpdates, sectionsToUpdate);
          if (parsedResponseText.errors) {
            this.#handleCartError(line, parsedResponseText);
            return;
          }
        }

        const cartItemsHtml = parsedResponseText.sections?.[this.sectionId];
        const newSectionHTML = cartItemsHtml
          ? new DOMParser().parseFromString(cartItemsHtml, 'text/html')
          : null;

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : parsedResponseText.item_count || 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (cartItemsHtml) {
          morphSection(this.sectionId, cartItemsHtml);
          this.#normalizeBundlePrices();
          this.#updateCartQuantitySelectorButtonStates();
          return;
        }

        await sectionRenderer.renderSection(this.sectionId, { cache: false });
        this.#normalizeBundlePrices();
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#normalizeBundlePrices();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #normalizeBundlePrices() {
    if (this.#bundlePriceFrame) window.cancelAnimationFrame(this.#bundlePriceFrame);

    this.#bundlePriceFrame = window.requestAnimationFrame(() => {
      const priceNodes = this.querySelectorAll(
        [
          'tr[data-bundle-role="parent"] .cart-items__price text-component[data-bundle-price-display]',
          'tr[data-bundle-role="child"] .cart-items__bundle-addon-price-inner text-component[data-bundle-price-display]',
        ].join(', ')
      );

      priceNodes.forEach((node) => {
        const displayPrice = node.getAttribute('data-bundle-price-display');
        if (!displayPrice) return;

        node.classList.remove('tpo_cart_item_price');

        if (node.textContent?.trim() !== displayPrice) {
          node.textContent = displayPrice;
        }
      });
    });
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Removes wizard add-ons left behind after their parent product is removed.
   * @param {Object} cart
   * @param {Array<{key: string, properties?: Record<string, string>}>} [cart.items]
   * @returns {Record<string, number>}
   */
  #buildOrphanWizardBundleUpdates(cart) {
    const parentKeys = new Set();
    const updates = {};

    for (const item of cart.items || []) {
      const properties = item.properties || {};
      if (properties._bundle_key && properties._bundle_role === 'parent') parentKeys.add(properties._bundle_key);
    }

    for (const item of cart.items || []) {
      const properties = item.properties || {};
      if (properties._bundle_key && properties._bundle_role === 'child' && !parentKeys.has(properties._bundle_key)) {
        updates[item.key] = 0;
      }
    }

    return updates;
  }

  /**
   * Updates cart items and returns the parsed cart response.
   * @param {Record<string, number>} updates
   * @param {Set<string>} sectionsToUpdate
   * @returns {Promise<Object>}
   */
  async #updateCartItems(updates, sectionsToUpdate) {
    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
    return JSON.parse(await response.text());
  }

  /**
   * Builds bundle quantity updates so parent and child lines stay in sync.
   * @param {HTMLTableRowElement} lineItemRow
   * @param {number} quantity
   * @returns {Record<string, number> | null}
   */
  #buildBundleUpdates(lineItemRow, quantity) {
    const { bundleKey, bundleRole, key } = lineItemRow.dataset;
    if (!bundleKey || bundleRole !== 'parent' || !key) return null;

    /** @type {Record<string, number>} */
    const updates = {
      [key]: quantity,
    };

    this.querySelectorAll(`tr[data-bundle-key="${bundleKey}"][data-bundle-role="child"]`).forEach((row) => {
      if (!(row instanceof HTMLTableRowElement)) return;
      const childKey = row.dataset.key;
      if (!childKey) return;
      updates[childKey] = quantity;
    });

    return updates;
  }

  #findCartItemRow(line) {
    return this.querySelector(`tr[data-line="${line}"]`);
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
